import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSim } from "@/context/SimContext";
import StepNav, { ANAESTHESIA2_STEPS } from "@/components/StepNav";
import AVARecord from "@/components/AVARecord2";
import { useAnaesthesiaSim, ISSUE_DEFS, outOfRange } from "@/hooks/useAnaesthesiaSim2";
import { getBreedVulnerabilities, getBreedWatch } from "@/data/breedVulnerabilities";
import { FurtherReading } from "@/components/FurtherReading";
import { DebriefCard } from "@/components/monitor2/DebriefCard";
import { SaveSession } from "@/components/SaveSession";
import { ComplicationsPanel } from "@/components/monitor2/ComplicationsPanel";
import {
  WaveformRow,
  Readout,
  ControlSlider,
  ManualEntry,
  InterventionBtn,
} from "@/components/monitor2/MonitorWidgets";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Heart,
  Activity,
  Wind,
  Thermometer,
  Play,
  Pause,
  RotateCcw,
  Droplet,
  HandHelping,
  Flame,
  Syringe,
  ChevronLeft,
  Gauge,
  ShieldCheck,
  StopCircle,
  Eye,
  AlertOctagon,
} from "lucide-react";
import { toast } from "sonner";

// ---------- Component ----------
export default function Monitor() {
  const navigate = useNavigate();
  const { patient, plan } = useSim();

  useEffect(() => {
    if (!patient || !plan) {
      toast.error("Complete patient and plan first.");
      navigate("/intake2");
    }
  }, [patient, plan, navigate]);

  const {
    vitals,
    setVitals,
    iso,
    setIso,
    o2flow,
    setO2flow,
    running,
    setRunning,
    speed,
    setSpeed,
    elapsed,
    issues,
    entries,
    events,
    markers,
    recovery,
    setRecovery,
    normalRange,
    activeIssueKeys,
    lifeState,
    criticality,
    dead,
    hrRef,
    rrRef,
    spo2AmpRef,
    ecgAmpRef,
    respAmpRef,
    triggerIssue,
    manualVent,
    giveAtropine,
    giveFluids,
    giveFentanyl,
    giveLidocaine,
    toggleWarming,
    setMarker,
    resetSim,
    computeDebrief,
  } = useAnaesthesiaSim(patient);

  const [debrief, setDebrief] = useState(null);
  const [randomMode, setRandomMode] = useState(false);

  // Complications this patient's breed is prone to — used to weight random mode.
  const vulnerabilities = getBreedVulnerabilities(patient);
  const breedWatch = getBreedWatch(patient);

  // Random complication mode: keep the latest issues + trigger fn + breed
  // vulnerabilities in refs so the interval effect stays subscribed only to
  // [randomMode, running].
  const issuesRef = useRef(issues);
  issuesRef.current = issues;
  const triggerRef = useRef(triggerIssue);
  triggerRef.current = triggerIssue;
  const vulnRef = useRef(vulnerabilities);
  vulnRef.current = vulnerabilities;

  // While armed and running, strike a random inactive complication periodically.
  // Selection is WEIGHTED toward the breed's vulnerabilities (4× more likely)
  // so the chosen case feels scenario-driven; if the breed has none it's uniform.
  useEffect(() => {
    if (!randomMode || !running) return undefined;
    const fire = () => {
      const inactive = Object.keys(ISSUE_DEFS).filter((k) => !issuesRef.current[k]);
      if (inactive.length === 0) return;
      const vulns = vulnRef.current.filter((k) => inactive.includes(k));
      const weighted = [];
      inactive.forEach((k) => {
        const w = vulns.includes(k) ? 4 : 1;
        for (let i = 0; i < w; i += 1) weighted.push(k);
      });
      const k = weighted[Math.floor(Math.random() * weighted.length)];
      triggerRef.current(k);
    };
    const first = setTimeout(fire, 6000);
    const interval = setInterval(fire, 22000);
    return () => {
      clearTimeout(first);
      clearInterval(interval);
    };
  }, [randomMode, running]);

  const handleReset = () => {
    setDebrief(null);
    resetSim();
  };

  const handleEndSession = () => {
    setRunning(false);
    setDebrief(computeDebrief());
    toast.message("Session ended — review your debrief below.");
  };

  // When the patient dies, automatically surface the debrief.
  useEffect(() => {
    if (lifeState === "dead") setDebrief(computeDebrief());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lifeState]);

  if (!patient || !plan) return null;

  // ----- Render -----
  return (
    <div className="min-h-screen bg-[#F4F7F9]">
      <StepNav current={3} steps={ANAESTHESIA2_STEPS} />
      <main className="max-w-[1500px] mx-auto px-4 py-6 fade-up">
        {lifeState !== "ok" && (
          <div
            data-testid="life-banner"
            className={`mb-5 rounded-lg border-2 px-5 py-4 flex items-center gap-3 ${
              dead
                ? "border-red-800 bg-red-950 text-red-50"
                : "border-red-500 bg-red-50 text-red-800 alarm-pulse"
            }`}
          >
            <AlertOctagon className="w-7 h-7 flex-shrink-0" />
            <div className="flex-1">
              <div className="text-base font-bold tracking-tight">
                {dead ? "PATIENT DECEASED" : "CARDIAC ARREST — ASYSTOLE"}
              </div>
              <div className="text-sm mt-0.5 opacity-90">
                {dead
                  ? "Resuscitation was unsuccessful. Reset to start a new case."
                  : "Resuscitate NOW: vaporiser OFF (Iso ≤ 1%), O₂ ≥ 2 L/min, deliver manual breaths, and give atropine IV."}
              </div>
            </div>
            {dead && (
              <Button
                size="sm"
                onClick={handleReset}
                data-testid="life-banner-reset"
                className="bg-white text-red-700 hover:bg-red-100"
              >
                <RotateCcw className="w-4 h-4 mr-1" /> New case
              </Button>
            )}
          </div>
        )}
        <div className="flex items-end justify-between mb-5 flex-wrap gap-3">
          <div>
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-[#0047AB] font-semibold mb-1">
              <Activity className="w-3.5 h-3.5" />
              <span>Live monitoring</span>
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
              Patient monitor — {patient.name}
            </h1>
            <p className="text-sm text-slate-600 mt-1">
              {patient.species === "cat" ? "Feline" : "Canine"} · {patient.breed} ·{" "}
              {patient.weightKg.toFixed(1)} kg · ASA {patient.asa}
            </p>
            {breedWatch && (
              <div
                data-testid="breed-watchpoints"
                className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-amber-300 bg-amber-50 px-2.5 py-1 text-[11px] font-semibold text-amber-800 max-w-full"
              >
                <Eye className="w-3.5 h-3.5 flex-shrink-0" />
                <span className="hidden sm:inline flex-shrink-0">Breed watch-points:</span>
                <span className="truncate">{breedWatch}</span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate("/plan2")}
              data-testid="back-to-plan-btn"
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Plan
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleReset}
              data-testid="reset-sim-btn"
            >
              <RotateCcw className="w-4 h-4 mr-1" />
              Reset
            </Button>
            <Button
              size="sm"
              onClick={() => setRunning((r) => !r)}
              data-testid="play-pause-btn"
              disabled={dead}
              className="bg-[#0047AB] hover:bg-[#003380] text-white disabled:opacity-50"
            >
              {running ? <Pause className="w-4 h-4 mr-1" /> : <Play className="w-4 h-4 mr-1" />}
              {running ? "Pause" : "Resume"}
            </Button>
            <Button
              size="sm"
              onClick={handleEndSession}
              data-testid="end-session-btn"
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              <StopCircle className="w-4 h-4 mr-1" />
              End & debrief
            </Button>
          </div>
        </div>

        {/* MONITOR (dark, ~50%) + trend / quick-reference side panel */}
        <div className="flex flex-col lg:flex-row gap-4 items-stretch">
        <div
          className="rounded-lg border-2 border-[#222] bg-[#050505] p-4 shadow-2xl w-full lg:w-1/2"
          data-testid="monitor-panel"
        >
          {/* Top alarm bar */}
          <div className="flex items-center justify-between mb-3 px-1">
            <div className="flex items-center gap-2">
              <div
                className={`w-2.5 h-2.5 rounded-full ${
                  lifeState !== "ok" || activeIssueKeys.length > 0 || criticality > 40
                    ? "bg-[#FF3333] alarm-pulse"
                    : "bg-[#39FF14]"
                }`}
              />
              <span className="text-[11px] uppercase tracking-widest text-slate-400 font-mono-clinical">
                {lifeState === "dead"
                  ? "Asystole — deceased"
                  : lifeState === "arrest"
                  ? "Cardiac arrest"
                  : activeIssueKeys.length > 0
                  ? activeIssueKeys.map((k) => ISSUE_DEFS[k].label).join(" · ")
                  : criticality > 40
                  ? `Patient unstable · risk ${criticality}%`
                  : "Stable"}
              </span>
            </div>
            <div className="flex items-center gap-4 text-[11px] text-slate-400 font-mono-clinical">
              <span>
                ELAPSED{" "}
                <span className="text-white">
                  {String(Math.floor(elapsed / 60)).padStart(2, "0")}:
                  {String(Math.floor(elapsed % 60)).padStart(2, "0")}
                </span>
              </span>
              <span>
                SPEED{" "}
                <button
                  onClick={() => setSpeed((s) => (s === 1 ? 5 : s === 5 ? 10 : 1))}
                  className="text-white underline decoration-dotted hover:text-[#39FF14]"
                  data-testid="speed-toggle-btn"
                >
                  {speed}×
                </button>
              </span>
            </div>
          </div>

          {/* Waveforms + Readouts grid */}
          <div className="grid grid-cols-12 gap-3">
            {/* Waveforms column */}
            <div className="col-span-12 lg:col-span-8 space-y-2">
              <WaveformRow
                label="ECG II"
                color="#39FF14"
                kind="ecg"
                rateRef={hrRef}
                amplitudeRef={ecgAmpRef}
              />
              <WaveformRow
                label="SpO₂ Pleth"
                color="#00FFFF"
                kind="spo2"
                rateRef={hrRef}
                amplitudeRef={spo2AmpRef}
              />
              <WaveformRow
                label="Resp"
                color="#FFEA00"
                kind="resp"
                rateRef={rrRef}
                amplitudeRef={respAmpRef}
              />
              <WaveformRow
                label="EtCO₂ Capnograph"
                color="#FFFFFF"
                kind="capno"
                rateRef={rrRef}
                amplitudeRef={respAmpRef}
              />
            </div>

            {/* Readouts column */}
            <div className="col-span-12 lg:col-span-4 grid grid-cols-2 gap-2">
              <Readout
                label="HR"
                unit="bpm"
                value={Math.round(vitals.hr)}
                color="#39FF14"
                alarm={outOfRange(vitals.hr, normalRange.hr)}
                Icon={Heart}
                testid="hr-readout"
              />
              <Readout
                label="SpO₂"
                unit="%"
                value={Math.round(vitals.spo2)}
                color="#00FFFF"
                alarm={vitals.spo2 < 92}
                Icon={Droplet}
                testid="spo2-readout"
              />
              <Readout
                label="RR"
                unit="/min"
                value={Math.round(vitals.rr)}
                color="#FFEA00"
                alarm={outOfRange(vitals.rr, normalRange.rr)}
                Icon={Wind}
                testid="rr-readout"
              />
              <Readout
                label="Temp"
                unit="°C"
                value={vitals.temp.toFixed(1)}
                color="#FFFFFF"
                alarm={outOfRange(vitals.temp, normalRange.temp)}
                Icon={Thermometer}
                testid="temp-readout"
              />
              <Readout
                label="MAP"
                unit="mmHg"
                value={Math.round(vitals.map)}
                color="#FF8C42"
                alarm={outOfRange(vitals.map, normalRange.map)}
                Icon={Gauge}
                testid="map-readout"
              />
              <Readout
                label="EtCO₂"
                unit="mmHg"
                value={Math.round(vitals.etco2)}
                color="#FFFFFF"
                alarm={vitals.etco2 > 55 || vitals.etco2 < 30}
                Icon={Activity}
                testid="etco2-readout"
              />
            </div>
          </div>
        </div>

          {/* Anaesthesia machine + interventions moved into the freed right-hand
              space, sized to match the main monitor display. */}
          <div className="w-full lg:w-1/2 flex flex-col gap-4" data-testid="monitor-side-panel">
            {/* Vaporizer + O2 */}
            <Card className="border-slate-200 p-5 flex-1" data-testid="vaporizer-card">
              <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-3">
                Anaesthesia machine
              </div>
              <div className="space-y-5">
                <ControlSlider
                  label="Isoflurane vaporizer"
                  value={iso}
                  onChange={(v) => setIso(v)}
                  min={0}
                  max={5}
                  step={0.1}
                  unit="%"
                  testid="iso-slider"
                  accent="#0047AB"
                  hint="Typical maintenance: 1.2–2.5 %"
                />
                <ControlSlider
                  label="Oxygen flow"
                  value={o2flow}
                  onChange={(v) => setO2flow(v)}
                  min={0}
                  max={4}
                  step={0.1}
                  unit="L/min"
                  testid="o2-slider"
                  accent="#0F766E"
                  hint="Typical carrier: 1–3 L/min"
                />
                <div className="grid grid-cols-2 gap-2 pt-1">
                  <ManualEntry
                    label="HR override"
                    value={Math.round(vitals.hr)}
                    onCommit={(n) => setVitals((v) => ({ ...v, hr: n }))}
                    testid="hr-override-input"
                  />
                  <ManualEntry
                    label="Temp override"
                    value={vitals.temp.toFixed(1)}
                    onCommit={(n) => setVitals((v) => ({ ...v, temp: n }))}
                    testid="temp-override-input"
                    step={0.1}
                  />
                </div>
              </div>
            </Card>

            {/* Interventions */}
            <Card className="border-slate-200 p-5 flex-1" data-testid="interventions-card">
              <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-3">
                Interventions
              </div>
              <div className="grid grid-cols-2 gap-2">
                <InterventionBtn
                  Icon={HandHelping}
                  label="Manual ventilation"
                  onClick={manualVent}
                  testid="manual-vent-btn"
                />
                <InterventionBtn
                  Icon={Flame}
                  label="Active warming"
                  onClick={toggleWarming}
                  testid="warming-btn"
                />
                <InterventionBtn
                  Icon={Syringe}
                  label="Atropine IV"
                  onClick={giveAtropine}
                  testid="atropine-btn"
                />
                <InterventionBtn
                  Icon={Droplet}
                  label="IV fluid bolus"
                  onClick={giveFluids}
                  testid="fluids-btn"
                />
                <InterventionBtn
                  Icon={Syringe}
                  label="Fentanyl IV"
                  onClick={giveFentanyl}
                  testid="fentanyl-btn"
                />
                <InterventionBtn
                  Icon={Syringe}
                  label="Lidocaine IV"
                  onClick={giveLidocaine}
                  testid="lidocaine-btn"
                />
              </div>
              <div className="mt-4 text-[11px] text-slate-500 leading-relaxed bg-slate-50 border border-slate-200 rounded-md p-3">
                <ShieldCheck className="w-3.5 h-3.5 inline mr-1 text-emerald-600" />
                Combine medication interventions with vaporizer / O₂ adjustments to stabilize the patient.
              </div>
            </Card>
          </div>
        </div>

        {/* Simulated complications */}
        <div className="mt-5" data-testid="complications-row">
          <ComplicationsPanel
            randomMode={randomMode}
            setRandomMode={setRandomMode}
            vulnerabilities={vulnerabilities}
            breed={patient.breed}
            running={running}
            activeIssueKeys={activeIssueKeys}
            issues={issues}
            triggerIssue={triggerIssue}
          />
        </div>

        {/* Procedure timeline & recovery (Sections 4 & 5) */}
        <Card className="border-slate-200 p-5 mt-5" data-testid="timeline-recovery-card">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Surgery / anaesthesia time markers */}
            <div>
              <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-3 flex items-center gap-1.5">
                <Activity className="w-3.5 h-3.5 text-[#0047AB]" />
                Procedure timeline
              </div>
              <p className="text-[11px] text-slate-500 mb-3">
                Mark surgical times at the current elapsed clock — they appear as vertical
                lines on the record. Anaesthesia start is logged automatically at 00:00.
              </p>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { key: "surgeryStart", label: "Surgery start" },
                  { key: "surgeryEnd", label: "Surgery end" },
                  { key: "extubation", label: "Extubation" },
                ].map((m) => (
                  <button
                    key={m.key}
                    type="button"
                    onClick={() => setMarker(m.key)}
                    data-testid={`marker-${m.key}-btn`}
                    disabled={markers[m.key] != null}
                    className={`rounded-md border-2 px-2 py-2.5 text-center transition-all ${
                      markers[m.key] != null
                        ? "border-emerald-300 bg-emerald-50 cursor-default"
                        : "border-slate-200 bg-white hover:border-[#0047AB] hover:bg-[#0047AB]/5"
                    }`}
                  >
                    <div className="text-xs font-semibold text-slate-800">{m.label}</div>
                    <div className="text-[11px] font-mono-clinical text-[#0047AB] mt-0.5">
                      {markers[m.key] != null
                        ? `${String(Math.floor(markers[m.key] / 60)).padStart(2, "0")}:${String(
                            Math.floor(markers[m.key] % 60)
                          ).padStart(2, "0")}`
                        : "— : —"}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Recovery scoring */}
            <div>
              <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-3 flex items-center gap-1.5">
                <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />
                Recovery & post-op
              </div>
              <div className="space-y-3">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Recovery quality</div>
                  <div className="flex flex-wrap gap-1.5">
                    {["Smooth", "Prolonged", "Dysphoric", "Rough"].map((q) => (
                      <button
                        key={q}
                        type="button"
                        onClick={() => setRecovery((r) => ({ ...r, quality: q }))}
                        data-testid={`recovery-quality-${q.toLowerCase()}`}
                        className={`text-xs font-semibold px-2.5 py-1.5 rounded-md border-2 transition-colors ${
                          recovery.quality === q
                            ? "border-[#0047AB] bg-[#0047AB]/5 text-[#0047AB]"
                            : "border-slate-200 text-slate-600 hover:border-slate-300"
                        }`}
                      >
                        {q}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="flex items-end gap-4">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Pain score (0–4)</div>
                    <div className="flex gap-1">
                      {[0, 1, 2, 3, 4].map((p) => (
                        <button
                          key={p}
                          type="button"
                          onClick={() => setRecovery((r) => ({ ...r, pain: String(p) }))}
                          data-testid={`recovery-pain-${p}`}
                          className={`w-8 h-8 rounded-md border-2 text-sm font-bold transition-colors ${
                            recovery.pain === String(p)
                              ? "border-[#0047AB] bg-[#0047AB]/5 text-[#0047AB]"
                              : "border-slate-200 text-slate-600 hover:border-slate-300"
                          }`}
                        >
                          {p}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="flex-1">
                    <Label className="text-[10px] uppercase tracking-wider text-slate-500">
                      Recovery analgesia
                    </Label>
                    <Input
                      value={recovery.analgesia}
                      onChange={(e) => setRecovery((r) => ({ ...r, analgesia: e.target.value }))}
                      data-testid="recovery-analgesia-input"
                      placeholder="e.g. methadone 0.2 mg/kg IV"
                      className="h-8 text-sm"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* End-of-session grading & debrief (inline) */}
        {debrief && (
          <>
            {debrief.died && (
              <div
                data-testid="debrief-death-note"
                className="mb-3 rounded-lg border-2 border-red-700 bg-red-50 px-5 py-4 text-red-800"
              >
                <div className="font-bold flex items-center gap-2">
                  <AlertOctagon className="w-5 h-5" /> Patient died during this session
                </div>
                <div className="text-sm mt-1">
                  The case ended in an unsurvivable anaesthetic event — recorded as a fatal outcome
                  (score 0 / grade F). Review the timeline below and reset to try again.
                </div>
              </div>
            )}
            <DebriefCard debrief={debrief} onDismiss={() => setDebrief(null)} />
            <SaveSession
              simulatorId="anaesthesia2"
              simulatorName="Veterinary Anaesthesia Simulator 2"
              score={debrief.score}
              grade={debrief.grade}
              summary={`Grade ${debrief.grade} · ${debrief.stabilityPct}% time-in-range`}
            />
          </>
        )}

        {/* Further reading — always available on this page */}
        <FurtherReading topic="anaesthesia" className="mt-4" />

        {/* AVA-style anaesthetic record */}
        <div className="mt-5">
          <AVARecord
            patient={patient}
            plan={plan}
            entries={entries}
            events={events}
            markers={markers}
            recovery={recovery}
            elapsedSec={elapsed}
          />
        </div>
      </main>
    </div>
  );
}
